// Phase F.1 — Customer Masters admin (forwarder-managed).
//
// Three modes managed by URL/state:
//   • list  — table of all customer_masters this forwarder owns.
//   • detail — open one master in a side-panel: edit fields + contacts CRUD.
//   • create — wizard to pick a linked customer tenant and create a master.
import React, { useMemo, useState } from "react";
import {
  Users, Plus, Trash2, Building2, Pencil, Save, X, Loader2,
  AlertCircle, Mail, Phone, Briefcase, Globe,
} from "lucide-react";
import { toast } from "sonner";
import {
  useCustomerMasters, useCustomerMaster, useCreateMaster, useUpdateMaster,
  useDeleteMaster, useCreateContact, useUpdateContact, useDeleteContact,
} from "../api/customerMasters";
import { useCustomerRelationships } from "../api/customerRelationships";
import RoutingRulesEditor from "../components/RoutingRulesEditor";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #0a0a0a)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const inputStyle = {
  background: "var(--bg, #ffffff)",
  border: "1px solid rgba(0,0,0,0.15)",
  color: "var(--text, #0a0a0a)",
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
  padding: "8px 10px",
  borderRadius: 5,
  width: "100%",
};

const ROLE_META = {
  logistics:  { color: "#7e3aa8", label: "Logistics"  },
  commercial: { color: "#1769aa", label: "Commercial" },
  accounting: { color: "#0a8754", label: "Accounting" },
  other:      { color: "#6b7388", label: "Other"      },
};

const RolePill = ({ role }) => {
  const m = ROLE_META[role] || ROLE_META.other;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded"
      style={{ background: `${m.color}1A`, color: m.color, border: `1px solid ${m.color}55`, ...mono(10, m.color, 700, "0.6px"), textTransform: "uppercase" }}
      data-testid={`contact-role-${role}`}
    >
      {m.label}
    </span>
  );
};

/* ─── Create-master modal ───────────────────────────────────────────── */

const CreateMasterModal = ({ open, onClose, existingMasterCustomerIds }) => {
  const create = useCreateMaster();
  const { data: relsData } = useCustomerRelationships();
  const customerOptions = useMemo(() => {
    const all = (relsData?.items || []).filter((r) => r.relation_type === "customer");
    // Exclude customers that already have a master from THIS forwarder.
    return all.filter((r) => !existingMasterCustomerIds.has(r.related_tenant_id));
  }, [relsData, existingMasterCustomerIds]);

  const [customer_tenant_id, setCustId] = useState("");
  const [payment_terms, setTerms] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [vat_id, setVat] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState(null);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!customer_tenant_id) { setErr("Pick a customer"); return; }
    try {
      await create.mutateAsync({
        customer_tenant_id,
        payment_terms: payment_terms || null,
        currency,
        vat_id: vat_id || null,
        notes: notes || null,
      });
      toast.success("Master created");
      setCustId(""); setTerms(""); setVat(""); setNotes(""); setCurrency("EUR");
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail?.message || e2?.response?.data?.detail || e2.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
      data-testid="create-master-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="rounded-md w-full max-w-lg p-5 space-y-3"
        style={{ background: "#ffffff", border: "1px solid rgba(0,0,0,0.12)" }}
      >
        <div className="flex items-center justify-between">
          <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 18 }}>
            <Plus size={16} className="inline mr-1" /> New customer master
          </h2>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}><X size={16} /></button>
        </div>
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Linked customer *</div>
          <select value={customer_tenant_id} onChange={(e) => setCustId(e.target.value)} style={inputStyle} data-testid="create-master-customer-select" required>
            <option value="">— pick one —</option>
            {customerOptions.map((r) => (
              <option key={r.related_tenant_id} value={r.related_tenant_id}>{r.related_name}</option>
            ))}
          </select>
          {customerOptions.length === 0 && (
            <div className="mt-1" style={mono(10, "#b3261e")}>No unmastered customers. Create a customer relationship first.</div>
          )}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Payment terms</div>
            <input value={payment_terms} onChange={(e) => setTerms(e.target.value)} placeholder="NET30" style={inputStyle} data-testid="create-master-terms" />
          </label>
          <label className="block">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Currency</div>
            <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))} style={inputStyle} data-testid="create-master-currency" />
          </label>
        </div>
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>VAT ID</div>
          <input value={vat_id} onChange={(e) => setVat(e.target.value)} placeholder="DE123456789" style={inputStyle} data-testid="create-master-vat" />
        </label>
        <label className="block">
          <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Notes</div>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} style={inputStyle} data-testid="create-master-notes" />
        </label>
        {err && (
          <div className="rounded px-3 py-2 inline-flex items-center gap-2"
               style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.40)", color: "#b3261e", ...mono(11), width: "100%" }}
               data-testid="create-master-error">
            <AlertCircle size={13} /> {String(err)}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} style={{ ...mono(11, "#6b7388", 700, "0.6px"), textTransform: "uppercase", background: "transparent", border: "1px solid rgba(0,0,0,0.15)", padding: "8px 12px", borderRadius: 5, cursor: "pointer" }}>Cancel</button>
          <button type="submit" disabled={create.isPending || !customer_tenant_id}
                  style={{ background: "#7e3aa8", color: "#fff", border: "none", padding: "8px 14px", borderRadius: 5, ...mono(11, "#fff", 700, "0.8px"), textTransform: "uppercase", cursor: "pointer", opacity: (create.isPending || !customer_tenant_id) ? 0.6 : 1 }}
                  data-testid="create-master-submit">
            {create.isPending ? <Loader2 size={12} className="animate-spin inline mr-1" /> : <Save size={12} className="inline mr-1" />}
            Create
          </button>
        </div>
      </form>
    </div>
  );
};

/* ─── Contact editor row ───────────────────────────────────────────── */

const ContactRow = ({ contact }) => {
  const update = useUpdateContact();
  const del = useDeleteContact();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...contact });

  const save = async () => {
    try {
      await update.mutateAsync({ contact_id: contact.id, ...form });
      setEditing(false); toast.success("Contact updated");
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e.message); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this contact?")) return;
    try { await del.mutateAsync(contact.id); toast.success("Contact deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e.message); }
  };

  if (editing) {
    return (
      <div className="rounded p-3 mb-2" style={{ background: "rgba(126,58,168,0.05)", border: "1px solid rgba(126,58,168,0.30)" }} data-testid={`contact-edit-${contact.id}`}>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" style={inputStyle} data-testid={`contact-name-${contact.id}`} />
          <input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" style={inputStyle} data-testid={`contact-email-${contact.id}`} />
          <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" style={inputStyle} />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={inputStyle} data-testid={`contact-role-select-${contact.id}`}>
            {Object.keys(ROLE_META).map((k) => <option key={k} value={k}>{ROLE_META[k].label}</option>)}
          </select>
          <select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })} style={inputStyle}>
            <option value="en">EN</option><option value="hu">HU</option><option value="de">DE</option>
          </select>
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={() => { setEditing(false); setForm({ ...contact }); }}
                  style={{ ...mono(10, "#6b7388"), background: "transparent", border: "1px solid rgba(0,0,0,0.15)", padding: "5px 10px", borderRadius: 4, cursor: "pointer" }}>Cancel</button>
          <button type="button" onClick={save} disabled={update.isPending}
                  style={{ ...mono(10, "#fff", 700, "0.8px"), textTransform: "uppercase", background: "#7e3aa8", color: "#fff", border: "none", padding: "5px 12px", borderRadius: 4, cursor: "pointer" }}
                  data-testid={`contact-save-${contact.id}`}>
            <Save size={11} className="inline mr-1" /> Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded p-3 mb-2 flex items-start gap-3" style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.10)" }} data-testid={`contact-row-${contact.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <strong style={mono(12)}>{contact.name}</strong>
          <RolePill role={contact.role} />
          <span style={{ ...mono(10, "#6b7388"), textTransform: "uppercase" }}>{contact.locale}</span>
        </div>
        <div style={mono(11, "#6b7388")} className="flex flex-wrap gap-3">
          {contact.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {contact.email}</span>}
          {contact.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {contact.phone}</span>}
        </div>
      </div>
      <div className="flex gap-1">
        <button type="button" onClick={() => setEditing(true)} style={{ background: "transparent", border: "none", color: "#6b7388", cursor: "pointer", padding: 4 }} data-testid={`contact-edit-btn-${contact.id}`}><Pencil size={13} /></button>
        <button type="button" onClick={remove} style={{ background: "transparent", border: "none", color: "#b3261e", cursor: "pointer", padding: 4 }} data-testid={`contact-delete-btn-${contact.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
};

/* ─── Detail panel ─────────────────────────────────────────────────── */

const DetailPanel = ({ masterId, onClose }) => {
  const { data: master, isLoading } = useCustomerMaster(masterId);
  const update = useUpdateMaster();
  const del = useDeleteMaster();
  const createContact = useCreateContact();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", role: "logistics", locale: "en" });
  const [showAddContact, setShowAddContact] = useState(false);

  // Form initialized on Edit click — not on master fetch. Avoids setting
  // state in effect and keeps the user's in-progress edits if the cache
  // refreshes underneath.

  if (!masterId) return null;

  const save = async () => {
    try {
      await update.mutateAsync({
        id: masterId,
        display_name: form.display_name,
        payment_terms: form.payment_terms,
        currency: form.currency,
        vat_id: form.vat_id,
        tax_id: form.tax_id,
        billing_address: form.billing_address,
        notes: form.notes,
        deviation_approver_user_ids: form.deviation_approver_user_ids || null,
        deviation_approval_mode: form.deviation_approval_mode || null,
        min_margin_pct: form.min_margin_pct === "" || form.min_margin_pct == null
          ? null
          : Number(form.min_margin_pct),
        internal_approval_amount_threshold:
          form.internal_approval_amount_threshold === "" || form.internal_approval_amount_threshold == null
            ? null
            : Number(form.internal_approval_amount_threshold),
        internal_approval_approver_user_ids: form.internal_approval_approver_user_ids || null,
        approval_sla_hours: form.approval_sla_hours === "" || form.approval_sla_hours == null ? null : Number(form.approval_sla_hours),
        approval_sla_hours_by_kind: form.approval_sla_hours_by_kind || null,
        secondary_approver_user_ids: form.secondary_approver_user_ids || null,
      });
      setEditing(false); toast.success("Master saved");
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e.message); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this master? Contacts will be deactivated.")) return;
    try { await del.mutateAsync(masterId); toast.success("Master deleted"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e.message); }
  };
  const addContact = async () => {
    if (!newContact.name) return;
    try {
      await createContact.mutateAsync({ master_id: masterId, ...newContact });
      setNewContact({ name: "", email: "", phone: "", role: "logistics", locale: "en" });
      setShowAddContact(false);
      toast.success("Contact added");
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e.message); }
  };

  return (
    <div className="rounded-md p-5" style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.12)" }} data-testid="master-detail-panel">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div style={{ ...mono(9, "#6b7388", 700, "1.2px"), textTransform: "uppercase" }}>Customer master</div>
          <h2 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 20 }}>
            <Building2 size={18} className="inline mr-2" />
            {master?.display_name || master?.customer_name}
          </h2>
        </div>
        <div className="flex gap-2">
          {!editing && (
            <>
              <button onClick={() => { setForm({ ...master }); setEditing(true); }} style={{ ...mono(10, "#7e3aa8", 700, "0.6px"), textTransform: "uppercase", background: "rgba(126,58,168,0.10)", border: "1px solid rgba(126,58,168,0.30)", padding: "6px 10px", borderRadius: 5, cursor: "pointer" }} data-testid="master-edit-btn"><Pencil size={11} className="inline mr-1" /> Edit</button>
              <button onClick={remove} style={{ ...mono(10, "#b3261e", 700, "0.6px"), textTransform: "uppercase", background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.30)", padding: "6px 10px", borderRadius: 5, cursor: "pointer" }} data-testid="master-delete-btn"><Trash2 size={11} className="inline mr-1" /> Delete</button>
            </>
          )}
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6b7388" }}><X size={16} /></button>
        </div>
      </div>

      {isLoading && <div style={mono(11, "#6b7388")}>Loading…</div>}
      {master && (
        <>
          {/* Fields */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              { k: "display_name", l: "Display name" },
              { k: "payment_terms", l: "Payment terms" },
              { k: "currency", l: "Currency" },
              { k: "vat_id", l: "VAT ID" },
              { k: "tax_id", l: "Tax ID" },
            ].map(({ k, l }) => (
              <label key={k} className="block">
                <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>{l}</div>
                {editing ? (
                  <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} style={inputStyle} data-testid={`master-field-${k}`} />
                ) : (
                  <div style={{ ...mono(12), padding: "8px 0" }}>{master[k] || <span style={mono(11, "#cbd5e1")}>—</span>}</div>
                )}
              </label>
            ))}
          </div>

          <div className="mb-4">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Billing address</div>
            {editing ? (
              <div className="grid grid-cols-5 gap-2">
                {["country", "city", "postal_code", "street", "house_number"].map((k) => (
                  <input key={k} value={form.billing_address?.[k] || ""}
                         onChange={(e) => setForm({ ...form, billing_address: { ...(form.billing_address || {}), [k]: e.target.value } })}
                         placeholder={k} style={inputStyle} data-testid={`master-addr-${k}`} />
                ))}
              </div>
            ) : (
              <div style={mono(12)}>
                {[master.billing_address?.street, master.billing_address?.house_number, master.billing_address?.postal_code, master.billing_address?.city, master.billing_address?.country]
                  .filter(Boolean).join(" ") || <span style={mono(11, "#cbd5e1")}>—</span>}
              </div>
            )}
          </div>

          <div className="mb-4" data-testid="master-deviation-approvers-block">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Contract-rate deviation approvers (J.1)</div>
            {editing ? (
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  value={(form.deviation_approver_user_ids || []).join(", ")}
                  onChange={(e) => setForm({ ...form, deviation_approver_user_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="Approver user_ids (comma-separated). Leave empty to use forwarder defaults."
                  style={inputStyle}
                  data-testid="master-deviation-approvers"
                />
                <div className="flex items-center gap-3" style={mono(11)}>
                  <span style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase" }}>Mode</span>
                  {["any_one", "all"].map((m) => (
                    <label key={m} className="inline-flex items-center gap-1">
                      <input
                        type="radio"
                        name="dev-mode"
                        value={m}
                        checked={(form.deviation_approval_mode || "any_one") === m}
                        onChange={() => setForm({ ...form, deviation_approval_mode: m })}
                        data-testid={`master-deviation-mode-${m}`}
                      />
                      {m === "any_one" ? "Any one decides (fast)" : "All must agree (strict)"}
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div style={mono(12)}>
                {(master.deviation_approver_user_ids || []).length === 0
                  ? <span style={mono(11, "#cbd5e1")}>— uses forwarder defaults</span>
                  : `${(master.deviation_approver_user_ids || []).length} assigned · mode: ${master.deviation_approval_mode || "any_one"}`}
              </div>
            )}
          </div>

          <div className="mb-4" data-testid="master-min-margin-block">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Minimum margin % (J.5b)</div>
            {editing ? (
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.min_margin_pct ?? ""}
                onChange={(e) => setForm({ ...form, min_margin_pct: e.target.value === "" ? null : e.target.value })}
                placeholder="e.g. 10.0 — leave empty for no gate (per-route lanes still apply)"
                style={inputStyle}
                data-testid="master-min-margin-input"
              />
            ) : (
              <div style={mono(12)} data-testid="master-min-margin-display">
                {master.min_margin_pct == null || master.min_margin_pct === ""
                  ? <span style={mono(11, "#cbd5e1")}>— no per-customer gate (per-route lanes only)</span>
                  : `${Number(master.min_margin_pct).toFixed(2)} % minimum margin`}
              </div>
            )}
          </div>

          <div className="mb-4" data-testid="master-internal-approval-block">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Customer-portal accept gate (J.5c)</div>
            {editing ? (
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={form.internal_approval_amount_threshold ?? ""}
                  onChange={(e) => setForm({ ...form, internal_approval_amount_threshold: e.target.value === "" ? null : e.target.value })}
                  placeholder="Amount threshold (e.g. 5000) — null for no gate"
                  style={inputStyle}
                  data-testid="master-internal-approval-threshold"
                />
                <input
                  type="text"
                  value={(form.internal_approval_approver_user_ids || []).join(",")}
                  onChange={(e) => setForm({ ...form, internal_approval_approver_user_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="Customer-tenant approver user_ids (comma-sep)"
                  style={inputStyle}
                  data-testid="master-internal-approval-approvers"
                />
              </div>
            ) : (
              <div style={mono(12)} data-testid="master-internal-approval-display">
                {master.internal_approval_amount_threshold == null
                  ? <span style={mono(11, "#cbd5e1")}>— no portal-accept gate (any-amount accepts go straight to won)</span>
                  : `Accept gate fires above ${Number(master.internal_approval_amount_threshold).toLocaleString()} · ${(master.internal_approval_approver_user_ids || []).length} approver(s)`}
              </div>
            )}
          </div>

          <div className="mb-4" data-testid="master-sla-block">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Approval SLA + escalation (J.5d)</div>
            {editing ? (
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.approval_sla_hours ?? ""}
                  onChange={(e) => setForm({ ...form, approval_sla_hours: e.target.value === "" ? null : Number(e.target.value) })}
                  placeholder="Default SLA hours (e.g. 8)"
                  style={inputStyle}
                  data-testid="master-sla-default-hours"
                />
                <input
                  type="text"
                  value={(form.secondary_approver_user_ids || []).join(",")}
                  onChange={(e) => setForm({ ...form, secondary_approver_user_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="Secondary approver user_ids (comma-sep)"
                  style={inputStyle}
                  data-testid="master-sla-secondary"
                />
                <input
                  type="text"
                  value={form.approval_sla_hours_by_kind ? JSON.stringify(form.approval_sla_hours_by_kind) : ""}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (!v) { setForm({ ...form, approval_sla_hours_by_kind: null }); return; }
                    try {
                      const parsed = JSON.parse(v);
                      setForm({ ...form, approval_sla_hours_by_kind: parsed });
                    } catch { /* don't crash on partial input */ }
                  }}
                  placeholder='Per-kind override (JSON, e.g. {"contract_deviation": 2})'
                  style={{ ...inputStyle, gridColumn: "1 / -1" }}
                  data-testid="master-sla-per-kind"
                />
              </div>
            ) : (
              <div style={mono(12)} data-testid="master-sla-display">
                {master.approval_sla_hours == null && !master.approval_sla_hours_by_kind
                  ? <span style={mono(11, "#cbd5e1")}>— no SLA timer (tasks won&apos;t auto-escalate)</span>
                  : (
                    <>
                      Default {master.approval_sla_hours ?? "—"} h ·{" "}
                      {(master.secondary_approver_user_ids || []).length} secondary approver(s)
                      {master.approval_sla_hours_by_kind && Object.keys(master.approval_sla_hours_by_kind).length > 0 && (
                        <span style={mono(10, "#94a3b8")}>
                          {" "}· per-kind: {JSON.stringify(master.approval_sla_hours_by_kind)}
                        </span>
                      )}
                    </>
                  )}
              </div>
            )}
          </div>

          <div className="mb-4">
            <div style={{ ...mono(9, "#6b7388", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>Notes</div>
            {editing ? (
              <textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={inputStyle} data-testid="master-field-notes" />
            ) : (
              <div style={mono(12)}>{master.notes || <span style={mono(11, "#cbd5e1")}>—</span>}</div>
            )}
          </div>

          {editing && (
            <div className="flex justify-end gap-2 mb-5">
              <button onClick={() => { setEditing(false); setForm({ ...master }); }} style={{ ...mono(11, "#6b7388", 700, "0.6px"), textTransform: "uppercase", background: "transparent", border: "1px solid rgba(0,0,0,0.15)", padding: "8px 12px", borderRadius: 5, cursor: "pointer" }}>Cancel</button>
              <button onClick={save} disabled={update.isPending} style={{ ...mono(11, "#fff", 700, "0.8px"), textTransform: "uppercase", background: "#7e3aa8", color: "#fff", border: "none", padding: "8px 14px", borderRadius: 5, cursor: "pointer", opacity: update.isPending ? 0.6 : 1 }} data-testid="master-save-btn">
                {update.isPending ? <Loader2 size={12} className="animate-spin inline mr-1" /> : <Save size={12} className="inline mr-1" />}
                Save
              </button>
            </div>
          )}

          {/* Contacts */}
          <div className="border-t pt-4" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <div className="flex items-center justify-between mb-3">
              <h3 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14 }}>
                <Briefcase size={14} className="inline mr-1" />
                Contacts ({(master.contacts || []).length})
              </h3>
              {!showAddContact && (
                <button onClick={() => setShowAddContact(true)} style={{ ...mono(10, "#7e3aa8", 700, "0.6px"), textTransform: "uppercase", background: "rgba(126,58,168,0.10)", border: "1px solid rgba(126,58,168,0.30)", padding: "5px 10px", borderRadius: 5, cursor: "pointer" }} data-testid="add-contact-btn">
                  <Plus size={11} className="inline mr-1" /> Add contact
                </button>
              )}
            </div>
            {showAddContact && (
              <div className="rounded p-3 mb-3" style={{ background: "rgba(126,58,168,0.05)", border: "1px solid rgba(126,58,168,0.30)" }} data-testid="add-contact-form">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="Name *" style={inputStyle} data-testid="new-contact-name" />
                  <input value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email" style={inputStyle} data-testid="new-contact-email" />
                  <input value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Phone" style={inputStyle} data-testid="new-contact-phone" />
                  <select value={newContact.role} onChange={(e) => setNewContact({ ...newContact, role: e.target.value })} style={inputStyle} data-testid="new-contact-role">
                    {Object.keys(ROLE_META).map((k) => <option key={k} value={k}>{ROLE_META[k].label}</option>)}
                  </select>
                  <select value={newContact.locale} onChange={(e) => setNewContact({ ...newContact, locale: e.target.value })} style={inputStyle} data-testid="new-contact-locale">
                    <option value="en">EN</option><option value="hu">HU</option><option value="de">DE</option>
                  </select>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowAddContact(false)} style={{ ...mono(10, "#6b7388"), background: "transparent", border: "1px solid rgba(0,0,0,0.15)", padding: "5px 10px", borderRadius: 4, cursor: "pointer" }}>Cancel</button>
                  <button onClick={addContact} disabled={!newContact.name || createContact.isPending}
                          style={{ ...mono(10, "#fff", 700, "0.8px"), textTransform: "uppercase", background: "#7e3aa8", color: "#fff", border: "none", padding: "5px 12px", borderRadius: 4, cursor: "pointer", opacity: (!newContact.name || createContact.isPending) ? 0.6 : 1 }}
                          data-testid="new-contact-save">
                    {createContact.isPending ? <Loader2 size={11} className="animate-spin inline mr-1" /> : null}
                    Add
                  </button>
                </div>
              </div>
            )}
            {(master.contacts || []).filter((c) => c.is_active !== false).length === 0 && !showAddContact && (
              <div className="text-center py-4" style={mono(11, "#6b7388")} data-testid="contacts-empty">
                No contacts yet. Add one to start using send-out routing.
              </div>
            )}
            {(master.contacts || []).filter((c) => c.is_active !== false).map((c) => (
              <ContactRow key={c.id} contact={c} />
            ))}
          </div>

          {/* Phase F.2 — Routing rules editor with test-rule preview */}
          <RoutingRulesEditor
            masterId={masterId}
            contacts={(master.contacts || []).filter((c) => c.is_active !== false)}
          />
        </>
      )}
    </div>
  );
};

/* ─── Page ─────────────────────────────────────────────────────────── */

export default function AdminCustomerMasters() {
  const { data, isLoading } = useCustomerMasters();
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const items = data?.items || [];
  const existingIds = useMemo(() => new Set(items.map((m) => m.customer_tenant_id)), [items]);

  return (
    <div data-testid="admin-customer-masters-page">
      <header className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 28px)" }}>
            <Users size={22} className="inline mr-2" />
            Customer Masters
          </h1>
          <div className="mt-1" style={mono(11, "#6b7388")}>
            Payment terms, billing address &amp; contacts for each linked customer.
            <Globe size={11} className="inline mx-1" /> Used by send-out routing.
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded"
          style={{ background: "#7e3aa8", color: "#fff", border: "none", ...mono(11, "#fff", 700, "0.8px"), textTransform: "uppercase", cursor: "pointer" }}
          data-testid="new-master-btn"
        >
          <Plus size={12} /> New master
        </button>
      </header>

      <div className="grid gap-4" style={{ gridTemplateColumns: selected ? "1fr 1fr" : "1fr" }}>
        <div className="rounded" style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.10)" }}>
          {isLoading && <div className="p-6 text-center" style={mono(11, "#6b7388")}>Loading…</div>}
          {!isLoading && items.length === 0 && (
            <div className="p-8 text-center" style={mono(12, "#6b7388")} data-testid="masters-empty">
              No customer masters yet. Click <strong>New master</strong> to create one.
            </div>
          )}
          {items.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(0,0,0,0.03)" }}>
                  {["Customer", "Display name", "Terms", "Currency", "Contacts"].map((h) => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", ...mono(9, "#6b7388", 700, "1.2px"), textTransform: "uppercase", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr
                    key={m.id}
                    data-testid={`master-row-${m.id}`}
                    onClick={() => setSelected(m.id)}
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid rgba(0,0,0,0.05)",
                      background: selected === m.id ? "rgba(126,58,168,0.06)" : "transparent",
                    }}
                  >
                    <td style={{ padding: "10px 12px", ...mono(11) }}>{m.customer_name}</td>
                    <td style={{ padding: "10px 12px", ...mono(11, "#475569") }}>{m.display_name || "—"}</td>
                    <td style={{ padding: "10px 12px", ...mono(11, "#475569") }}>{m.payment_terms || "—"}</td>
                    <td style={{ padding: "10px 12px", ...mono(11, "#475569") }}>{m.currency || "EUR"}</td>
                    <td style={{ padding: "10px 12px", ...mono(11, "#475569") }}>{m.contacts_count ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {selected && (
          <DetailPanel masterId={selected} onClose={() => setSelected(null)} />
        )}
      </div>

      <CreateMasterModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        existingMasterCustomerIds={existingIds}
      />
    </div>
  );
}
